import httpClient from '@/services/httpClient';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface Category {
  id: number;
  name: string;
  description: string | null;
  courseCount: number;
  creatorId?: string;
  createdAt?: string;
  updatedAt?: string;
  isExpanded?: boolean;
  subcategories?: Category[];
}

export const getCategories = async (): Promise<{
  success: boolean;
  data?: Category[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Category[]>>('/super-admin/catalog/categories');

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch categories' }
    };
  }
};

// ============================================================================
// CATALOG CATEGORY CRUD OPERATIONS
// ============================================================================

/**
 * Create a new category
 */
export const createCategory = async (
  name: string,
  description?: string
): Promise<{
  success: boolean;
  data?: Category;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Category>>(
      '/super-admin/catalog/categories',
      { name, description: description || null }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create category',
      },
    };
  }
};

/**
 * Update a category
 */
export const updateCategory = async (
  categoryId: number,
  name: string,
  description?: string
): Promise<{
  success: boolean;
  data?: Category;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<Category>>(
      `/super-admin/catalog/categories/${categoryId}`,
      { name, description: description || null }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update category',
      },
    };
  }
};

/**
 * Delete a category
 */
export const deleteCategory = async (categoryId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/super-admin/catalog/categories/${categoryId}`
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete category',
      },
    };
  }
};

/**
 * Create a new subcategory
 */
export const createSubcategory = async (
  categoryId: number,
  name: string,
  description?: string
): Promise<{
  success: boolean;
  data?: Category;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Category>>(
      '/super-admin/catalog/subcategories',
      { categoryId, name, description: description || null }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create subcategory',
      },
    };
  }
};

/**
 * Update a subcategory
 */
export const updateSubcategory = async (
  subcategoryId: number,
  name: string,
  description?: string
): Promise<{
  success: boolean;
  data?: Category;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<Category>>(
      `/super-admin/catalog/subcategories/${subcategoryId}`,
      { name, description: description || null }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update subcategory',
      },
    };
  }
};

/**
 * Delete a subcategory
 */
export const deleteSubcategory = async (subcategoryId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/super-admin/catalog/subcategories/${subcategoryId}`
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete subcategory',
      },
    };
  }
};

// ============================================================================
// CATALOG COURSES - VIEW OPERATIONS
// ============================================================================

export interface CatalogCourse {
  id: string;
  title: string;
  category: string;
  subcategory?: string | null;
  level: string;
  duration: string;
  thumbnail?: string | null;
  description?: string | null;
  rating: string;
  enrolledCount: number;
}

export interface FeaturedCourse {
  id: string;
  title: string;
  category: string;
  rating: number;
  enrolled: number;
  isActive: boolean;
}

export interface RecommendationRule {
  id: string;
  rule_name: string;
  status: 'Active' | 'Inactive';
  rule_condition: string;
  courses: Array<{ id: string; title: string }>;
}

/**
 * Get catalog courses with optional filters
 */
export const getCatalogCourses = async (filters?: {
  categoryId?: number;
  subcategoryId?: number;
  level?: string;
  minDuration?: number;
  maxDuration?: number;
  search?: string;
}): Promise<{
  success: boolean;
  data?: CatalogCourse[];
  error?: { message: string } | null;
}> => {
  try {
    // Build query parameters
    const params = new URLSearchParams();
    if (filters?.categoryId) params.append('categoryId', filters.categoryId.toString());
    if (filters?.subcategoryId) params.append('subcategoryId', filters.subcategoryId.toString());
    if (filters?.level) params.append('level', filters.level);
    if (filters?.minDuration) params.append('minDuration', filters.minDuration.toString());
    if (filters?.maxDuration) params.append('maxDuration', filters.maxDuration.toString());
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `/super-admin/catalog/courses${queryString ? `?${queryString}` : ''}`;

    const response = await httpClient.get<ApiResponse<CatalogCourse[]>>(url);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch catalog courses' }
    };
  }
};

/**
 * Get featured courses
 */
export const getFeaturedCourses = async (): Promise<{
  success: boolean;
  data?: FeaturedCourse[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<FeaturedCourse[]>>('/super-admin/catalog/featured-courses');

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch featured courses' }
    };
  }
};

/**
 * Get recommendation rules
 */
export const getRecommendationRules = async (): Promise<{
  success: boolean;
  data?: RecommendationRule[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<RecommendationRule[]>>('/super-admin/catalog/recommendation-rules');

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch recommendation rules' }
    };
  }
};

// ============================================================================
// FEATURED COURSES - CRUD OPERATIONS
// ============================================================================

export interface ActiveCourse {
  id: string;
  title: string;
  description?: string;
}

/**
 * Get active courses for searchable dropdown
 */
export const getActiveCourses = async (search?: string): Promise<{
  success: boolean;
  data?: ActiveCourse[];
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    const queryString = params.toString();
    const url = `/super-admin/catalog/active-courses${queryString ? `?${queryString}` : ''}`;

    const response = await httpClient.get<ApiResponse<ActiveCourse[]>>(url);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error?.message || 'Failed to fetch active courses' }
    };
  }
};

/**
 * Add a featured course
 */
export const addFeaturedCourse = async (courseId: number): Promise<{
  success: boolean;
  data?: FeaturedCourse;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<FeaturedCourse>>(
      '/super-admin/catalog/featured-courses',
      { courseId }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add featured course',
      },
    };
  }
};

/**
 * Toggle featured course status
 */
export const toggleFeaturedCourse = async (featuredId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.patch<ApiResponse<null>>(
      `/super-admin/catalog/featured-courses/${featuredId}/toggle`
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to toggle featured course',
      },
    };
  }
};

/**
 * Remove a featured course
 */
export const removeFeaturedCourse = async (featuredId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/super-admin/catalog/featured-courses/${featuredId}`
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to remove featured course',
      },
    };
  }
};

// ============================================================================
// RECOMMENDATION RULES - CRUD OPERATIONS
// ============================================================================

/**
 * Add a new recommendation rule
 */
export const addRecommendationRule = async (
  ruleName: string,
  ruleCondition: string,
  courseIds: number[]
): Promise<{
  success: boolean;
  data?: RecommendationRule;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<RecommendationRule>>(
      '/super-admin/catalog/recommendation-rules',
      { rule_name: ruleName, rule_condition: ruleCondition, courseIds }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add recommendation rule',
      },
    };
  }
};

/**
 * Update a recommendation rule
 */
export const updateRecommendationRule = async (
  ruleId: number,
  ruleName: string,
  ruleCondition: string,
  courseIds: number[]
): Promise<{
  success: boolean;
  data?: RecommendationRule;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<RecommendationRule>>(
      `/super-admin/catalog/recommendation-rules/${ruleId}`,
      { rule_name: ruleName, rule_condition: ruleCondition, courseIds }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update recommendation rule',
      },
    };
  }
};

/**
 * Toggle recommendation rule status
 */
export const toggleRecommendationRuleStatus = async (ruleId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.patch<ApiResponse<null>>(
      `/super-admin/catalog/recommendation-rules/${ruleId}/toggle`
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to toggle recommendation rule status',
      },
    };
  }
};

/**
 * Delete a recommendation rule
 */
export const deleteRecommendationRule = async (ruleId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      `/super-admin/catalog/recommendation-rules/${ruleId}`
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete recommendation rule',
      },
    };
  }
};
